import React, { useCallback, useEffect, useState } from 'react';

import {
  Add as AddIcon,
  Cancel as CancelIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { Box, Button, IconButton, MenuItem, Select, Stack, TextField, Typography } from '@mui/material';

import { definitions } from '@luxuryescapes/contract-svc-promo';

import { PromoMeta } from '~/services/PromoService';

import PromoDisplayConditionType from './PromoDisplayConditionType';
import PromoDisplayConditionTypeSelect from './PromoDisplayConditionTypeSelect';

type Props = {
  conditionType: 'global' | 'displayOptionSpecific';
  conditions: definitions['RuleJson']['conditions'] | undefined;
  promoMeta: PromoMeta;
  onConditionsChange?: (updatedConditions: definitions['RuleJson']['conditions']) => void;
  disabled?: boolean;
};

type ConditionRowData = definitions['RuleJson']['conditions'][0] & {
  id: number;
  isNew?: boolean;
  config: PromoMeta['promo_display_config_conditions'][0] | undefined;
};

const PromoDisplayConfigConditions = ({
  conditionType,
  conditions,
  promoMeta,
  onConditionsChange,
  disabled,
}: Props) => {
  const [rows, setRows] = useState<ConditionRowData[]>(
    conditions?.map((condition, index) => ({
      ...condition,
      id: index,
      config: promoMeta.promo_display_config_conditions.find((config) => config.field === condition.field),
    })) || [],
  );
  const [editingRowId, setEditingRowId] = useState<number | null>(null);

  // Sync local state when conditions prop changes
  useEffect(() => {
    setRows(
      conditions?.map((condition, index) => ({
        ...condition,
        id: index,
        config: promoMeta.promo_display_config_conditions.find((config) => config.field === condition.field),
      })) || [],
    );
  }, [conditions]);

  const handleEdit = useCallback((id: number) => {
    setEditingRowId(id);
  }, []);

  const handleSave = useCallback(
    (id: number) => {
      const row = rows.find((r) => r.id === id);
      if (!row) return;

      // Validate that the value field is not empty
      if (!row.value || row.value.trim() === '') {
        return; // Don't save if value is empty
      }

      setEditingRowId(null);
      // Notify parent of changes
      const updatedConditions = rows.map(({ id, isNew, ...row }) => row);
      onConditionsChange?.(updatedConditions);
    },
    [rows, onConditionsChange],
  );

  const handleCancel = useCallback(
    (id: number) => {
      setEditingRowId(null);
      // If it was a new row, remove it
      const row = rows.find((r) => r.id === id);
      if (row?.isNew) {
        const updatedRows = rows.filter((row) => row.id !== id);
        setRows(updatedRows);
        const updatedConditions = updatedRows.map(({ id, isNew, ...row }) => row);
        onConditionsChange?.(updatedConditions);
      }
    },
    [rows, onConditionsChange],
  );

  const handleDelete = useCallback(
    (id: number) => {
      const updatedRows = rows.filter((row) => row.id !== id);
      setRows(updatedRows);
      const updatedConditions = updatedRows.map(({ id, isNew, ...row }) => row);
      onConditionsChange?.(updatedConditions);
    },
    [rows, onConditionsChange],
  );

  const handleAddNew = useCallback(() => {
    const newId = rows.length > 0 ? Math.max(...rows.map((row) => row.id as number), -1) + 1 : 0;
    const newRow: ConditionRowData = {
      id: newId,
      field: 'mpv',
      conditionType: 'gt',
      value: '',
      isNew: true,
      config: promoMeta.promo_display_config_conditions.find((config) => config.field === 'mpv'),
    };
    setRows([...rows, newRow]);
    setEditingRowId(newId);
  }, [rows]);

  const handleFieldChange = useCallback(
    (id: number, field: keyof ConditionRowData, value: any) => {
      const updatedRows = rows.map((row) => (row.id === id ? { ...row, [field]: value } : row));
      setRows(updatedRows);
    },
    [rows],
  );

  return (
    <Box sx={{ minWidth: 400, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <Stack direction="row" justifyContent="flex-end" mb={1} sx={{ width: '100%' }}>
        <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={handleAddNew} disabled={disabled}>
          Add {conditionType === 'global' ? 'Global' : ''} Condition
        </Button>
      </Stack>

      <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        {rows.map((row) => {
          const isEditing = editingRowId === row.id;
          return (
            <Stack
              key={row.id}
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ justifyContent: 'center', width: '100%' }}
            >
              <Box>
                {isEditing ? (
                  <Select
                    required
                    size="medium"
                    value={row.field}
                    onChange={(e) => handleFieldChange(row.id, 'field', e.target.value)}
                    sx={{ minWidth: 120, fontSize: '0.75rem' }}
                  >
                    {promoMeta.promo_display_config_conditions.map((option) => (
                      <MenuItem key={option.field} value={option.field} sx={{ fontSize: '0.75rem' }}>
                        {option.fieldName ?? option.field}
                      </MenuItem>
                    ))}
                  </Select>
                ) : (
                  row.config?.fieldName ?? row.field
                )}
              </Box>
              <Box>
                {isEditing ? (
                  <PromoDisplayConditionTypeSelect
                    size="medium"
                    value={row.conditionType}
                    conditionTypes={
                      promoMeta.promo_display_config_conditions.find((condition) => condition.field === row.field)
                        ?.conditionTypes
                    }
                    onChange={(e) => handleFieldChange(row.id, 'conditionType', e)}
                  />
                ) : (
                  <PromoDisplayConditionType conditionType={row.conditionType} />
                )}
              </Box>
              <Box>
                {isEditing ? (
                  <TextField
                    size="medium"
                    value={row.value}
                    required
                    error={!row.value || row.value.trim() === ''}
                    helperText={!row.value || row.value.trim() === '' ? 'Value is required' : ''}
                    onChange={(e) => handleFieldChange(row.id, 'value', e.target.value)}
                  />
                ) : (
                  <Typography variant="body2">{row.value}</Typography>
                )}
              </Box>
              <Box sx={{ fontSize: '0.75rem' }}>
                {isEditing ? (
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <IconButton size="medium" onClick={() => handleSave(row.id)} color="primary">
                      <SaveIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="medium" onClick={() => handleCancel(row.id)} color="inherit">
                      <CancelIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <IconButton size="small" onClick={() => handleEdit(row.id)} color="primary">
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDelete(row.id)} color="error">
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                )}
              </Box>
            </Stack>
          );
        })}
      </Box>
    </Box>
  );
};

export default PromoDisplayConfigConditions;
